interface Message {
  role: 'user' | 'assistant' | 'system';
  content: string;
}

interface ChatCompletionOptions {
  model?: string;
  temperature?: number;
  max_tokens?: number;
  top_p?: number;
  frequency_penalty?: number;
  presence_penalty?: number;
  stop?: string[];
}

export class OpenAIService {
  private static instance: OpenAIService;
  private defaultModel = 'openai/gpt-4o-mini';
  private supabase: any;

  private constructor() {
    // Initialize Supabase client immediately
    this.initializeSupabase();
  }

  private async initializeSupabase() {
    try {
      const module = await import('@/integrations/supabase/client');
      this.supabase = module.supabase;
      // Supabase client initialized silently
    } catch (error) {
      console.error('Failed to initialize Supabase client:', error);
    }
  }

  public static getInstance(): OpenAIService {
    if (!OpenAIService.instance) {
      OpenAIService.instance = new OpenAIService();
    }
    return OpenAIService.instance;
  }

  private lastRequestTime: number = 0;
  private requestQueue: Array<() => void> = [];
  private isProcessingQueue = false;

  private getFallbackResponse(): string {
    const fallbacks = [
      "That's an interesting point! I'd love to hear more about your thoughts on this.",
      "Thanks for sharing that perspective. Let me think about it...",
      "I appreciate your input! Let's explore this further.",
      "Great point! What else is on your mind?",
      "I see what you're saying. Have you considered any next steps?",
      "That's a valuable insight. Let's discuss this more.",
      "I'm here to help. Could you tell me more about this?",
      "Interesting! How did you come to that conclusion?"
    ];
    return fallbacks[Math.floor(Math.random() * fallbacks.length)];
  }

  private async processQueue() {
    if (this.isProcessingQueue || this.requestQueue.length === 0) return;
    
    this.isProcessingQueue = true;
    const now = Date.now();
    const timeSinceLastRequest = now - this.lastRequestTime;
    
    // Rate limit to 1 request per second
    if (timeSinceLastRequest < 1000) {
      await new Promise(resolve => setTimeout(resolve, 1000 - timeSinceLastRequest));
    }
    
    const nextRequest = this.requestQueue.shift();
    if (nextRequest) {
      this.lastRequestTime = Date.now();
      nextRequest();
    }
    
    this.isProcessingQueue = false;
    
    // Process next in queue if any
    if (this.requestQueue.length > 0) {
      setTimeout(() => this.processQueue(), 0);
    }
  }

  async createChatCompletion(
    messages: Message[],
    options: ChatCompletionOptions = {}
  ): Promise<string> {
    // If we're already rate limited, return a fallback response immediately
    if (this.requestQueue.length > 5) {
      console.warn('Request queue full. Using fallback response.');
      return this.getFallbackResponse();
    }

    return new Promise((resolve) => {
      // Add the request to the queue
      this.requestQueue.push(async () => {
        try {
          if (!this.supabase) {
            try {
              await this.initializeSupabase();
              if (!this.supabase) {
                throw new Error('Failed to initialize Supabase client');
              }
            } catch (error) {
              console.error('Failed to initialize Supabase client:', error);
              resolve(this.getFallbackResponse());
              return;
            }
          }

          console.log('Calling Supabase function with messages:', JSON.stringify(messages, null, 2));
          
          const { data, error } = await this.supabase.functions.invoke('openrouter-ai', {
            body: {
              messages,
              model: options.model || this.defaultModel,
              temperature: options.temperature ?? 0.7,
              max_tokens: options.max_tokens,
              top_p: options.top_p,
              frequency_penalty: options.frequency_penalty,
              presence_penalty: options.presence_penalty,
              stop: options.stop
            }
          });

          console.log('Supabase function response:', { data, error });

          if (error || !data) {
            console.error('OpenRouter Edge Function error:', error || 'No data returned');
            resolve(this.getFallbackResponse());
            return;
          }

          // Handle different response formats
          const content = data.choices?.[0]?.message?.content || data?.result || data;
          
          if (!content) {
            console.error('No content in response:', data);
            resolve(this.getFallbackResponse());
            return;
          }

          resolve(content);
        } catch (error) {
          console.error('Error in createChatCompletion:', error);
          resolve(this.getFallbackResponse());
        } finally {
          // Process next in queue
          this.processQueue();
        }
      });

      // Start processing the queue if not already doing so
      if (!this.isProcessingQueue) {
        this.processQueue();
      }
    });
  }
}

export const openAIService = OpenAIService.getInstance();
